(() => {
  // COMPONENT: FreeGiftPromotions - evaluates repeatable merchant-defined free-gift rules and keeps the cart aligned across page, drawer, and cart template flows.
  const selectors = {
    config: '[data-free-gift-promotions-config]',
    checkout: 'button[name="checkout"], input[name="checkout"]',
    message: '[data-free-gift-message]',
  };

  const checkoutAttributeKeys = {
    status: 'SK Gift Verification Status',
    issue: 'SK Gift Verification Issue',
    checkedAt: 'SK Gift Verification Checked At',
  };

  const propertyKeys = {
    autoAdded: '_sk_free_gift_auto',
    ruleId: '_sk_free_gift_rule',
    source: '_sk_free_gift_source',
  };

  const giftSources = {
    productQuantity: 'product_quantity',
    cartValue: 'cart_value',
  };

  let rules = [];
  let latestCartState = null;
  let syncTimer = null;
  let syncInFlight = false;
  let rerunQueued = false;
  let syncPending = false;
  let dispatchingPromoCartEvents = false;
  let cartPageConfigTexts = [];
  let cartPageConfigRequest = null;
  let cartPageConfigResolved = false;
  let cartPageConfigFailed = false;
  let configReady = false;
  let checkoutVerificationInFlight = false;
  let bypassCheckoutVerifier = false;
  const checkoutVerifiers = [];

  const publicApi = window.SteakKingFreeGiftPromotions || {};
  window.SteakKingFreeGiftPromotions = publicApi;

  publicApi.isSyncPending = () => syncPending;
  publicApi.registerCheckoutVerifier = (verifier) => {
    if (typeof verifier !== 'function' || checkoutVerifiers.indexOf(verifier) !== -1) return;
    checkoutVerifiers.push(verifier);
  };

  const setSyncPending = (isPending) => {
    if (syncPending === isPending) return;

    syncPending = isPending;

    document.dispatchEvent(
      new CustomEvent('sk:free-gift:sync-state', {
        detail: {
          pending: syncPending,
        },
        bubbles: true,
      })
    );
  };

  const normalizeProductHandle = (value) =>
    String(value || '')
      .toLowerCase()
      .trim();

  const getRootUrl = () => {
    const configuredRoot = window.theme && window.theme.routes ? window.theme.routes.root_url : '/';
    return configuredRoot || '/';
  };

  const getCartUrl = () => `${getRootUrl()}cart.js`;
  const getCartPageUrl = () => `${getRootUrl()}cart`;
  const getCartAddUrl = () =>
    (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || `${getRootUrl()}cart/add.js`;
  const getCartUpdateUrl = () => `${getRootUrl()}cart/update.js`;

  const isCartPage = () => /(^|\/)cart\/?$/.test(window.location.pathname || '');

  const getLocalConfigTexts = () =>
    Array.prototype.slice.call(document.querySelectorAll(selectors.config)).map((configNode) => {
      return configNode.textContent || '{}';
    });

  // DATA: This theme can render promotion rules from the cart template and the global footer, so the engine merges every config payload instead of trusting only the first node.
  const collectRulesFromConfigTexts = (configTexts) => {
    const ruleMap = new Map();

    configTexts.forEach((configText) => {
      const parsedConfig = JSON.parse(configText || '{}');
      const sectionId = String((parsedConfig && parsedConfig.sectionId) || '').trim();
      const parsedRules = Array.isArray(parsedConfig && parsedConfig.rules) ? parsedConfig.rules : [];

      parsedRules
        .map((rule) => normalizeRule(rule, sectionId))
        .filter((rule) => rule && rule.enabled && rule.qualifyingProductId && rule.giftVariantId)
        .forEach((rule) => {
          if (!ruleMap.has(rule.ruleKey)) {
            ruleMap.set(rule.ruleKey, rule);
          }
        });
    });

    return Array.from(ruleMap.values());
  };

  // DATA: Non-cart pages intentionally render an empty global config; fetch the cart page once so staff still edit product gift rules only in the cart template.
  const loadCartPageConfig = () => {
    if (cartPageConfigRequest || cartPageConfigResolved || isCartPage()) return cartPageConfigRequest;

    setSyncPending(true);

    cartPageConfigRequest = fetch(getCartPageUrl(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart page config request failed with status ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        const cartDocument = new DOMParser().parseFromString(html, 'text/html');
        cartPageConfigTexts = Array.prototype.slice
          .call(cartDocument.querySelectorAll(selectors.config))
          .map((configNode) => configNode.textContent || '{}');
      })
      .catch(() => {
        cartPageConfigFailed = true;
        cartPageConfigTexts = [];
      })
      .finally(() => {
        cartPageConfigResolved = true;
        cartPageConfigRequest = null;
        readConfig();
        scheduleSync();
      });

    return cartPageConfigRequest;
  };

  const readConfig = () => {
    const configTexts = getLocalConfigTexts().concat(cartPageConfigTexts);

    try {
      rules = collectRulesFromConfigTexts(configTexts);
    } catch (error) {
      rules = [];
    }

    configReady = Boolean(rules.length || isCartPage() || cartPageConfigResolved);

    if (!configReady) {
      loadCartPageConfig();
    }

    renderPromoMessage(latestCartState);
  };

  const buildRuleKey = (rule) => {
    return [
      normalizeProductHandle(rule.qualifyingProductHandle) || 'no-handle',
      parseInt(rule.qualifyingProductId, 10) || 0,
      parseInt(rule.requiredQuantity, 10) || 0,
      normalizeProductHandle(rule.giftProductHandle) || 'no-gift-handle',
      parseInt(rule.giftProductId, 10) || 0,
      parseInt(rule.giftVariantId, 10) || 0,
      parseInt(rule.giftQuantity, 10) || 0,
      rule.allowMultiples ? 'multi' : 'single',
    ].join('::');
  };

  const normalizeRule = (rule, sectionId) => {
    if (!rule || typeof rule !== 'object') return null;

    const requiredQuantity = Math.max(parseInt(rule.requiredQuantity, 10) || 0, 1);
    const giftQuantity = Math.max(parseInt(rule.giftQuantity, 10) || 0, 1);
    const qualifyingProductId = parseInt(rule.qualifyingProductId, 10) || 0;
    const giftVariantId = parseInt(rule.giftVariantId, 10) || 0;
    const giftProductId = parseInt(rule.giftProductId, 10) || 0;
    const allowMultiples = Boolean(rule.allowMultiples);

    if (!qualifyingProductId || !giftVariantId) return null;

    const normalizedRule = {
      blockId: String(rule.blockId || ''),
      sectionId: String(sectionId || ''),
      enabled: Boolean(rule.enabled),
      ruleName: String(rule.ruleName || '').trim(),
      qualifyingProductHandle: normalizeProductHandle(rule.qualifyingProductHandle),
      qualifyingProductId,
      qualifyingProductTitle: String(rule.qualifyingProductTitle || '').trim(),
      requiredQuantity,
      giftProductHandle: normalizeProductHandle(rule.giftProductHandle),
      giftProductId,
      giftProductTitle: String(rule.giftProductTitle || '').trim(),
      giftVariantId,
      giftVariantAvailable: Boolean(rule.giftVariantAvailable),
      giftQuantity,
      allowMultiples,
      lockedMessage: String(rule.lockedMessage || '').trim(),
      unlockedMessage: String(rule.unlockedMessage || '').trim(),
    };

    normalizedRule.ruleKey = buildRuleKey(normalizedRule);
    return normalizedRule;
  };

  const fetchCart = () =>
    fetch(getCartUrl(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(handleJsonResponse);

  const postJson = (url, payload) =>
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(handleJsonResponse);

  const handleJsonResponse = (response) => {
    if (!response.ok) {
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  };

  const getLineProperty = (item, key) => {
    if (!item || !item.properties || typeof item.properties !== 'object') return '';
    return String(item.properties[key] || '');
  };

  const isAutoGiftLine = (item) =>
    getLineProperty(item, propertyKeys.autoAdded) === 'true' || getLineProperty(item, propertyKeys.ruleId) !== '';

  const isProductQuantityGiftLine = (item) => {
    const source = getLineProperty(item, propertyKeys.source);

    return source === '' || source === giftSources.productQuantity;
  };

  const isRuleGiftLine = (item, rule) =>
    item.variant_id === rule.giftVariantId &&
    getLineProperty(item, propertyKeys.autoAdded) === 'true' &&
    getLineProperty(item, propertyKeys.ruleId) === rule.ruleKey;

  const isManagedGiftVariant = (item) => {
    if (!item) return false;

    return rules.some((rule) => item.variant_id === rule.giftVariantId);
  };

  // RULE: Gift-card-only carts should not show physical-product promotion prompts.
  const hasShippingRequiredItems = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return true;

    return cart.items.some((item) => item && item.requires_shipping !== false && !isAutoGiftLine(item));
  };

  const getUnauthorizedGiftLines = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => {
      const coveredByActiveRule = rules.some((rule) => isRuleGiftLine(item, rule));

      if (isAutoGiftLine(item)) {
        return isProductQuantityGiftLine(item) && !coveredByActiveRule;
      }

      return isManagedGiftVariant(item);
    });
  };

  const getQualifyingQuantity = (cart, rule) => {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      if (rule.qualifyingProductHandle) {
        if (normalizeProductHandle(item.handle) !== rule.qualifyingProductHandle) return total;
      } else if (item.product_id !== rule.qualifyingProductId) {
        return total;
      }

      if (isAutoGiftLine(item)) return total;
      return total + (parseInt(item.quantity, 10) || 0);
    }, 0);
  };

  const getGiftLinesForRule = (cart, rule) => {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => isRuleGiftLine(item, rule));
  };

  const getGiftLineQuantity = (giftLines) =>
    giftLines.reduce((total, item) => total + (parseInt(item.quantity, 10) || 0), 0);

  const getTargetGiftQuantity = (qualifyingQuantity, rule) => {
    if (!rule.giftVariantAvailable || qualifyingQuantity < rule.requiredQuantity) return 0;

    if (!rule.allowMultiples) {
      return rule.giftQuantity;
    }

    return Math.floor(qualifyingQuantity / rule.requiredQuantity) * rule.giftQuantity;
  };

  const getRuleStatus = (cart, rule) => {
    const qualifyingQuantity = getQualifyingQuantity(cart, rule);
    const giftLines = getGiftLinesForRule(cart, rule);
    const existingGiftQuantity = getGiftLineQuantity(giftLines);
    const targetGiftQuantity = getTargetGiftQuantity(qualifyingQuantity, rule);
    const remainingQuantity =
      qualifyingQuantity >= rule.requiredQuantity ? 0 : rule.requiredQuantity - qualifyingQuantity;

    return {
      rule,
      qualifyingQuantity,
      giftLines,
      existingGiftQuantity,
      targetGiftQuantity,
      remainingQuantity,
      isEligible: targetGiftQuantity > 0,
    };
  };

  const getGiftProperties = (rule) => ({
    [propertyKeys.autoAdded]: 'true',
    [propertyKeys.ruleId]: rule.ruleKey,
    [propertyKeys.source]: giftSources.productQuantity,
  });

  const getGiftAuditIssues = (cart) => {
    if (!cart || !Array.isArray(cart.items) || !rules.length) return [];

    const ruleIssues = rules
      .map((rule) => {
        const status = getRuleStatus(cart, rule);

        if (status.targetGiftQuantity === status.existingGiftQuantity) return null;

        return {
          source: giftSources.productQuantity,
          type: status.targetGiftQuantity > status.existingGiftQuantity ? 'missing' : 'excess',
          ruleKey: rule.ruleKey,
          ruleName: rule.ruleName || rule.qualifyingProductTitle,
          giftTitle: rule.giftProductTitle,
          expected: status.targetGiftQuantity,
          actual: status.existingGiftQuantity,
        };
      })
      .filter(Boolean);

    const unauthorizedIssues = getUnauthorizedGiftLines(cart).map((line) => ({
      source: giftSources.productQuantity,
      type: 'unauthorized',
      ruleKey: getLineProperty(line, propertyKeys.ruleId),
      ruleName: 'Unqualified gift line',
      giftTitle: line.product_title || line.title || 'gift',
      expected: 0,
      actual: parseInt(line.quantity, 10) || 0,
    }));

    return ruleIssues.concat(unauthorizedIssues);
  };

  const formatGiftIssue = (issue) => {
    const ruleName = issue.ruleName || issue.ruleKey || 'Gift rule';
    const giftTitle = issue.giftTitle || 'gift';

    return [
      issue.source || 'gift',
      issue.type || 'mismatch',
      ruleName,
      giftTitle,
      `expected ${issue.expected}`,
      `actual ${issue.actual}`,
    ].join(' - ');
  };

  const getGiftIssueSummary = (issues) => {
    if (!issues.length) return '';

    const summary = issues.map(formatGiftIssue).join(' | ');
    return summary.length > 450 ? `${summary.slice(0, 447)}...` : summary;
  };

  const collectGiftLineUpdates = (lineUpdates, giftLines, targetQuantity) => {
    let quantityToKeep = targetQuantity;

    giftLines.forEach((line) => {
      const lineQuantity = parseInt(line.quantity, 10) || 0;
      const nextQuantity = Math.max(Math.min(lineQuantity, quantityToKeep), 0);
      quantityToKeep = Math.max(quantityToKeep - nextQuantity, 0);

      if (nextQuantity !== lineQuantity) {
        lineUpdates.set(line.key, nextQuantity);
      }
    });
  };

  const postLineUpdates = (lineUpdates) => {
    if (!lineUpdates.size) return Promise.resolve(null);

    const updates = {};
    lineUpdates.forEach((quantity, lineKey) => {
      updates[lineKey] = quantity;
    });

    return postJson(getCartUpdateUrl(), { updates });
  };

  const postGiftAdditions = (giftAdditions) => {
    if (!giftAdditions.length) return Promise.resolve(null);

    return postJson(getCartAddUrl(), {
      items: giftAdditions,
    });
  };

  const commitGiftOperations = (lineUpdates, giftAdditions) => {
    const operations = [];

    if (lineUpdates.size) {
      operations.push(() => postLineUpdates(lineUpdates));
    }

    if (giftAdditions.length) {
      operations.push(() => postGiftAdditions(giftAdditions));
    }

    if (!operations.length) {
      return Promise.resolve(false);
    }

    return operations.reduce((chain, operation) => chain.then(operation), Promise.resolve()).then(() => true);
  };

  const applyRuleOperations = (cart) => {
    if (!cart || !Array.isArray(cart.items)) {
      return Promise.resolve(false);
    }

    if (!rules.length && cartPageConfigFailed && !isCartPage()) {
      return Promise.resolve(false);
    }

    const lineUpdates = new Map();
    const giftAdditions = [];
    const unauthorizedGiftLines = getUnauthorizedGiftLines(cart);

    unauthorizedGiftLines.forEach((line) => {
      lineUpdates.set(line.key, 0);
    });

    if (!rules.length) {
      return commitGiftOperations(lineUpdates, giftAdditions);
    }

    rules.forEach((rule) => {
      const status = getRuleStatus(cart, rule);

      if (status.targetGiftQuantity > status.existingGiftQuantity) {
        giftAdditions.push({
          id: rule.giftVariantId,
          quantity: status.targetGiftQuantity - status.existingGiftQuantity,
          properties: getGiftProperties(rule),
        });
      }

      if (status.targetGiftQuantity < status.existingGiftQuantity) {
        collectGiftLineUpdates(lineUpdates, status.giftLines, status.targetGiftQuantity);
      }
    });

    return commitGiftOperations(lineUpdates, giftAdditions);
  };

  const waitForCurrentSync = () =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        if (!syncPending && !syncInFlight && !syncTimer) {
          resolve();
          return;
        }

        if (Date.now() - startedAt > 1500) {
          resolve();
          return;
        }

        window.setTimeout(check, 50);
      };

      check();
    });

  const runRegisteredCheckoutVerifiers = (cart, repair) =>
    checkoutVerifiers.reduce(
      (chain, verifier) =>
        chain.then((state) =>
          Promise.resolve(verifier({ cart: state.cart, repair }))
            .then((result) => {
              const verifierIssues = result && Array.isArray(result.issues) ? result.issues : [];
              const verifierChanged = Boolean(result && result.changed);

              if (!verifierChanged) {
                return {
                  cart: state.cart,
                  changed: state.changed,
                  issues: state.issues.concat(verifierIssues),
                };
              }

              return fetchCart().then((updatedCart) => {
                latestCartState = updatedCart;

                return {
                  cart: updatedCart,
                  changed: true,
                  issues: state.issues.concat(verifierIssues),
                };
              });
            })
            .catch(() => ({
              cart: state.cart,
              changed: state.changed,
              issues: state.issues.concat([
                {
                  source: 'checkout_verifier',
                  type: 'error',
                  ruleName: 'Registered gift verifier',
                  giftTitle: 'gift',
                  expected: 'checked',
                  actual: 'failed',
                },
              ]),
            }))
        ),
      Promise.resolve({ cart, changed: false, issues: [] })
    );

  const writeCheckoutVerificationAttributes = (issues) => {
    const attributes = {};

    if (issues.length) {
      attributes[checkoutAttributeKeys.status] = 'Packing team ATT';
      attributes[checkoutAttributeKeys.issue] = getGiftIssueSummary(issues);
      attributes[checkoutAttributeKeys.checkedAt] = new Date().toISOString();
    } else {
      attributes[checkoutAttributeKeys.status] = '';
      attributes[checkoutAttributeKeys.issue] = '';
      attributes[checkoutAttributeKeys.checkedAt] = '';
    }

    return postJson(getCartUpdateUrl(), { attributes }).catch(() => null);
  };

  const runCheckoutVerification = () => {
    if (!configReady) {
      loadCartPageConfig();
    }

    return Promise.resolve(cartPageConfigRequest)
      .then(waitForCurrentSync)
      .then(() => {
        setSyncPending(true);
      })
      .then(fetchCart)
      .then((cart) => {
        latestCartState = cart;
        return applyRuleOperations(cart).then((changed) => {
          if (!changed) return cart;
          return fetchCart().then((updatedCart) => {
            latestCartState = updatedCart;
            return updatedCart;
          });
        });
      })
      .then((cart) => runRegisteredCheckoutVerifiers(cart, true))
      .then((state) => {
        const productIssues = getGiftAuditIssues(state.cart);

        return runRegisteredCheckoutVerifiers(state.cart, false).then((finalState) => {
          const issues = productIssues.concat(finalState.issues);
          return writeCheckoutVerificationAttributes(issues).then(() => finalState.cart);
        });
      })
      .catch((error) =>
        writeCheckoutVerificationAttributes([
          {
            source: 'checkout_verifier',
            type: 'error',
            ruleName: 'Checkout gift verification',
            giftTitle: 'gift',
            expected: 'checked',
            actual: error && error.message ? error.message : 'failed',
          },
        ])
      );
  };

  const getCheckoutTrigger = (event) => {
    if (event.type === 'submit') {
      return event.submitter && event.submitter.matches(selectors.checkout) ? event.submitter : null;
    }

    return event.target && event.target.closest(selectors.checkout);
  };

  const continueCheckout = (checkoutTrigger) => {
    if (!checkoutTrigger) return;

    const checkoutForm = checkoutTrigger.form || checkoutTrigger.closest('form');
    bypassCheckoutVerifier = true;

    if (checkoutForm && typeof checkoutForm.requestSubmit === 'function') {
      checkoutForm.requestSubmit(checkoutTrigger);
    } else if (checkoutForm && typeof checkoutForm.submit === 'function') {
      checkoutForm.submit();
    } else if (typeof checkoutTrigger.click === 'function') {
      checkoutTrigger.click();
    }

    window.setTimeout(() => {
      bypassCheckoutVerifier = false;
    }, 0);
  };

  const handleCheckoutStart = (event) => {
    const checkoutTrigger = getCheckoutTrigger(event);
    if (!checkoutTrigger || bypassCheckoutVerifier) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (checkoutVerificationInFlight) return;

    checkoutVerificationInFlight = true;

    runCheckoutVerification().finally(() => {
      checkoutVerificationInFlight = false;
      setSyncPending(false);
      continueCheckout(checkoutTrigger);
    });
  };

  const dispatchCartEvents = (cart) => {
    dispatchingPromoCartEvents = true;

    try {
      document.dispatchEvent(
        new CustomEvent('theme:cart:change', {
          detail: {
            cart,
            source: 'free-gift-promotions',
          },
          bubbles: true,
        })
      );

      document.dispatchEvent(
        new CustomEvent('theme:cart:reload', {
          detail: {
            source: 'free-gift-promotions',
          },
          bubbles: true,
        })
      );
    } finally {
      dispatchingPromoCartEvents = false;
    }
  };

  const applyMessageState = (container, state) => {
    if (!container) return;

    if (!state || !state.text) {
      container.textContent = '';
      container.hidden = true;
      container.classList.add('is-hidden');
      container.classList.remove('is-success');
      return;
    }

    container.textContent = state.text;
    container.hidden = false;
    container.classList.remove('is-hidden');
    container.classList.toggle('is-success', state.type === 'success');
  };

  const formatRuleMessage = (template, status) => {
    return template
      .replace(/\|\|product\|\|/g, status.rule.qualifyingProductTitle)
      .replace(/\|\|gift\|\|/g, status.rule.giftProductTitle)
      .replace(/\|\|required\|\|/g, String(status.rule.requiredQuantity))
      .replace(/\|\|remaining\|\|/g, String(status.remainingQuantity))
      .replace(/\|\|gift_quantity\|\|/g, String(status.rule.giftQuantity));
  };

  const getMessageState = (cart) => {
    if (!cart || !rules.length) return null;
    if (!hasShippingRequiredItems(cart)) return null;

    const statuses = rules.map((rule) => getRuleStatus(cart, rule));
    const eligibleStatus = statuses.find((status) => status.isEligible && status.rule.unlockedMessage);

    if (eligibleStatus) {
      return {
        text: formatRuleMessage(eligibleStatus.rule.unlockedMessage, eligibleStatus),
        type: 'success',
      };
    }

    const pendingStatus = statuses.find(
      (status) => !status.isEligible && status.remainingQuantity > 0 && status.rule.lockedMessage
    );

    if (!pendingStatus) return null;

    return {
      text: formatRuleMessage(pendingStatus.rule.lockedMessage, pendingStatus),
      type: 'info',
    };
  };

  const renderPromoMessage = (cart) => {
    const state = getMessageState(cart);
    document.querySelectorAll(selectors.message).forEach((container) => {
      applyMessageState(container, state);
    });
  };

  const runSync = () => {
    syncTimer = null;

    if (!configReady) {
      loadCartPageConfig();
      return;
    }

    if (syncInFlight) {
      rerunQueued = true;
      setSyncPending(true);
      return;
    }

    syncInFlight = true;
    setSyncPending(true);

    Promise.resolve(latestCartState || fetchCart())
      .then((cart) => {
        latestCartState = cart;
        return applyRuleOperations(cart).then((cartChanged) => {
          if (!cartChanged) {
            renderPromoMessage(cart);
            return cart;
          }

          return fetchCart().then((updatedCart) => {
            latestCartState = updatedCart;
            renderPromoMessage(updatedCart);
            dispatchCartEvents(updatedCart);
            return updatedCart;
          });
        });
      })
      .catch(() => {
        renderPromoMessage(latestCartState);
      })
      .finally(() => {
        syncInFlight = false;

        if (rerunQueued) {
          rerunQueued = false;
          scheduleSync();
          return;
        }

        setSyncPending(false);
      });
  };

  const scheduleSync = (cart) => {
    if (cart && typeof cart === 'object') {
      latestCartState = cart;
    }

    if (!configReady) {
      loadCartPageConfig();
      return;
    }

    if (syncTimer) {
      window.clearTimeout(syncTimer);
    }

    setSyncPending(true);
    syncTimer = window.setTimeout(runSync, 75);
  };

  // EVENT: Theme cart updates emit these custom events already, so the promo engine hooks into the same lifecycle instead of replacing the existing AJAX cart flow.
  document.addEventListener('theme:cart:change', (event) => {
    if (dispatchingPromoCartEvents || (event.detail && event.detail.source === 'free-gift-promotions')) {
      if (event.detail && event.detail.cart) {
        latestCartState = event.detail.cart;
        renderPromoMessage(latestCartState);
      }
      return;
    }

    scheduleSync(event.detail && event.detail.cart);
  });

  document.addEventListener('theme:cart:reload', (event) => {
    if (dispatchingPromoCartEvents || (event.detail && event.detail.source === 'free-gift-promotions')) return;

    window.setTimeout(() => {
      renderPromoMessage(latestCartState);
      scheduleSync();
    }, 220);
  });

  document.addEventListener('shopify:section:load', () => {
    readConfig();
    window.setTimeout(() => {
      renderPromoMessage(latestCartState);
      scheduleSync();
    }, 120);
  });

  document.addEventListener('shopify:section:unload', () => {
    readConfig();
    renderPromoMessage(latestCartState);
  });

  // SAFETY: Checkout gets one last soft verification pass; remaining mismatches are written to order attributes for staff instead of blocking payment.
  document.addEventListener('click', handleCheckoutStart, true);
  document.addEventListener('submit', handleCheckoutStart, true);

  document.addEventListener('DOMContentLoaded', () => {
    readConfig();
    scheduleSync();
  });
})();
